

import { mapGetters } from 'vuex';
import { removeWhitespaces } from '@sopheos/orbjs_rules';

// API
import * as apiContributions from '../api/private/contributions';

import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SDetailDescription',
  mixins: [errorsMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    hasContribution: {
      type: Boolean,
      default: false,
    },
    contributions: {
      type: Object,
      default: null,
    },
    loadingContributions: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Object,
      default: () => ({}),
    },
    hideAppBar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fieldsRefs: ['contribution-description'],
      editDescription: false,
      waitEditDescription: false,
      newDescription: null,
      contributionValue: null,
      toggleTooltip: false,
      timeoutId: null,
      loading: false,
      showDeleteContribution: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuth: 'authToken/isAuth',
      userSecured: 'preferences/userSecured',
    }),
    descIsSet() {
      // The description is not null and is not empty
      return this.newDescription?.length > 0;
    },
    descIsValid() {
      // The description is set and contains between 10 and 400 characters
      return this.descIsSet && this.newDescription.length >= 10 && this.newDescription.length <= 400;
    },
    descriptionEmpty() {
      const empty = [
        'Aucune description renseignée pour le moment.',
        'La description n\'a pas été renseignée.',
        'Il manque des informations sur cette aire de jeux.',
        'Les détails ne nous ont pas encore été communiqués pour cette aire de jeux.',
        'La description est manquante.',
        'Pour le moment, nous n\'avons pas de description.',
      ];

      const cta = [
        'Soyez le premier à nous la renseigner !',
        'Aidez-nous à compléter ces informations en renseignant la description.',
        'Si vous connaissez l\'aire de jeux, n\'hésitez pas à la décrire.',
        'Gagnez des niveaux de contributeur en complétant la description !',
        'Contribuez en nous décrivant cette aire de jeux.',
        'Votre aide est la bienvenue pour renseigner cette information manquante.',
        'Merci de nous aider à compléter cette fiche en remplissant la description.',
        'Vous connaissez l\'aire de jeux ? Partagez ce que vous savez avec la communauté !',
      ];

      return `${empty[Math.floor(Math.random() * empty.length)]} ${cta[Math.floor(Math.random() * cta.length)]}`;
    },
  },
  watch: {
    id() {
      this.closeDescriptionForm();
    },
    editDescription(editDescription) {
      if (!editDescription) {
        // reset errors when leaving edition
        this.errors$.reset();
        // deactivate hideOnScroll
        this.$emit('update:hide-app-bar', false);
        setTimeout(() => {
          if (!this.visible.description) {
            // scroll to the top of the "description" block if it is not visible
            this.$refs.detailDescription.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setTimeout(() => {
              // reactivate hideOnScroll
              this.$emit('update:hide-app-bar', true);
            }, 500);
          } else {
            // reactivate hideOnScroll
            this.$emit('update:hide-app-bar', true);
          }
        }, 500);
      }
      this.$emit('has-unsave', editDescription);
    },
    isAuth(value) {
      if (!value) {
        this.editDescription = false;
      }
    },
    hasContribution() {
      this.contributionValue = this.hasContribution;
    },
    loadingContributions() {
      if (!this.loadingContributions && this.userSecured && this.waitEditDescription) {
        // After logging in, call the API to get the contribution data
        if (this.contributionValue && this.contributions === null) {
          this.$emit('get-contributions');
          return;
        }
        this.newDescription = this.contributions?.description ? this.contributions.description : this.description;
        this.editDescription = true;
        this.waitEditDescription = false;
        this.$nextTick(() => {
          if (this.$refs['contribution-description']) {
            this.$refs['contribution-description'].focus();
          }
        });
      }
    },
    newDescription() {
      // When the description changes, if it is not set or if it is valid, set the error to false
      if (!this.descIsSet || this.descIsValid) {
        // Due to a conflict with the swiper, when the clear button is used,
        // the focus is lost and the blur event is triggered
        // Because of this, the timeout of the checkError function must be cleared
        clearTimeout(this.timeoutId$);
        this.errors$.handleError(false, 'contribution', 'description');
      }
    },
  },
  mounted() {
    this.contributionValue = this.hasContribution;
  },
  methods: {
    showLogin() {
      if (!this.userSecured) {
        this.$emit('login');
        this.waitEditDescription = true;
      }
    },
    showDescriptionForm() {
      this.showLogin();
      if (!this.waitEditDescription) {
        // If they are not yet set, call the API to get the contribution data
        if (!this.newDescription && this.contributionValue && this.contributions === null) {
          this.waitEditDescription = true;
          this.$emit('get-contributions');
          return;
        }
        this.newDescription = this.contributions?.description ? this.contributions.description : this.description;
        this.editDescription = true;
        this.$nextTick(() => {
          if (this.$refs['contribution-description']) {
            this.$refs['contribution-description'].focus();
          }
        });
      }
    },
    closeDescriptionForm() {
      this.editDescription = false;
      this.waitEditDescription = false;
      this.newDescription = null;
    },
    editDescriptionForm() {
      this.newDescription = removeWhitespaces(this.newDescription);

      // prevalidation
      this.errors$.reset();
      this.errors$.handleError(!this.descIsValid, 'contribution', 'description');
      if (this.errors$.hasErrors()) {
        this.errors$.focusFirstError();
        return;
      }

      this.loading = true;
      const data = {
        playground: this.id,
        description: this.newDescription,
      };
      apiContributions.contribution(data, this)
        .then(() => {
          this.closeDescriptionForm();
          this.contributionValue = true;
          this.$emit('contribution', data.description);
        }).catch((error) => {
          // No change on this field. Just close the form
          if (this.errors$.getCode('contribution', 'form') === 'no_change') {
            this.closeDescriptionForm();
            return;
          }

          // Validation error
          if (error.response && error.response.status === 400) {
            if (this.errors$.hasErrors()) {
              this.errors$.focusFirstError();
              return;
            }
            this.errors$.handleError(true, 'contribution', 'description');
          }
        }).finally(() => {
          this.loading = false;
        });
    },
    deleteContribution() {
      this.closeDescriptionForm();
      this.contributionValue = false;
      this.$emit('delete-contribution');
    },
  },
};

